import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  getCoachTeams,
  getClubSessions,
  getCoachMetricsReminder,
  type ClubSession,
  type CoachTeam,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameCard, GameHero } from "../../../components/game";
import { useSportNames } from "../../../components/SportChips";

interface TeamStats {
  id: string;
  name: string;
  athleteCount: number;
  sport_type: string | null;
  age_group: string | null;
}

interface DashboardAthlete {
  id: string;
  full_name: string;
  team_id: string;
}

export default function AppCoachDashboard() {
  const { t } = useTranslation();
  const sportName = useSportNames();
  const { user, clubs: ctxClubs, activeClubId: ctxClubId, setActiveClubId } = useAuth();

  const [teams, setTeams] = useState<TeamStats[]>([]);
  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [athletes, setAthletes] = useState<DashboardAthlete[]>([]);
  const [metricsNeeded, setMetricsNeeded] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const currentClub = ctxClubs.find(c => c.id === ctxClubId) || ctxClubs[0];
  const activeClubId = ctxClubId || ctxClubs[0]?.id;

  useEffect(() => {
    const loadData = async () => {
      if (!activeClubId || !user?.id) return;
      setLoading(true);
      setError(false);
      try {
        const [coachTeamsData, sessionsData] = await Promise.all([
          getCoachTeams(user.id),
          getClubSessions(activeClubId),
        ]);

        // Map teams with athlete counts from coach teams data
        const teamStats: TeamStats[] = coachTeamsData.map((team: CoachTeam) => ({
          id: team.id,
          name: team.name,
          athleteCount: team.athlete_count,
          sport_type: team.sport_type,
          age_group: team.age_group,
        }));

        // Flatten athletes from all teams
        const allAthletes: DashboardAthlete[] = coachTeamsData.flatMap((team: CoachTeam) =>
          team.athletes.map(a => ({
            id: a.id,
            full_name: a.full_name,
            team_id: team.id,
          }))
        );

        setTeams(teamStats);
        setAthletes(allAthletes);

        // Filter to upcoming sessions only and only sessions for coach's teams
        const coachTeamIds = coachTeamsData.map(t => t.id);
        const now = new Date();
        const upcoming = sessionsData
          .filter(s => new Date(s.start_at) > now && coachTeamIds.includes(s.team_id))
          .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
        setSessions(upcoming);
      } catch (error) {
        console.error("Failed to load coach data:", error);
        setError(true);
      }
      setLoading(false);
    };
    loadData();
    // Best-effort: how many athletes still need their physical metrics
    // recorded/updated — drives the home alert. Never blocks the dashboard.
    getCoachMetricsReminder()
      .then((r) => setMetricsNeeded(r.athletes_needing_update || 0))
      .catch(() => setMetricsNeeded(0));
  }, [activeClubId, user?.id]);

  // Get today's sessions
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todaySessions = sessions.filter(s => {
    const sessionDate = new Date(s.start_at);
    return sessionDate >= today && sessionDate < tomorrow;
  });

  // Get next session
  const nextSession = sessions[0];

  // Calculate total athletes
  const totalAthletes = athletes.length;

  // Today's sessions that still have no attendance recorded — a coach's most
  // time-sensitive task, surfaced as a home alert.
  const todayNeedsAttendance = todaySessions.filter((s) => !(s._count?.attendance)).length;

  // A failed fetch used to render the dashboard with every stat at zero,
  // indistinguishable from a brand-new coach — show a visible error + retry.
  if (!loading && error) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6">
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        </div>
      </MobileAppLayout>
    );
  }

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Hero — no greeting (the app header already greets); instead show
            today's date + a live summary, then the two primary actions. */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          {currentClub?.name && (
            <p className="text-white/60 text-[11px] font-bold uppercase tracking-wide truncate">{currentClub.name}</p>
          )}
          <h1 className="text-xl font-extrabold leading-tight capitalize">
            {new Date().toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
          </h1>
          <p className="text-white/70 text-[13px] mt-0.5">
            {todaySessions.length > 0
              ? t("coach.homeTodayCount", "{{n}} antrenamente azi", { n: todaySessions.length })
              : t("coach.homeNoToday", "Niciun antrenament azi")}
            {totalAthletes > 0 && ` · ${totalAthletes} ${t("coach.athletes", "Sportivi").toLowerCase()}`}
          </p>
          <div className="flex gap-2 mt-3">
            <Link
              to="/app/coach/sessions?action=new"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl font-bold text-[13px] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              {t("coach.newSession", "Sesiune")}
            </Link>
            <Link
              to="/app/coach/athletes?action=invite"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl font-bold text-[13px] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
              {t("coach.inviteAthlete", "Invită")}
            </Link>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-5">
        {/* Club switcher — for a coach who works with more than one club. All
            club-scoped data below follows the selected club. */}
        {ctxClubs.length > 1 && (
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">{t("coach.activeClub", "Club activ")}</label>
            <select
              value={activeClubId || ""}
              onChange={(e) => setActiveClubId(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {ctxClubs.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Alerts — what needs the coach's attention right now. */}
        {(todayNeedsAttendance > 0 || metricsNeeded > 0) && (
          <div className="space-y-2.5">
            {todayNeedsAttendance > 0 && (
              <Link
                to="/app/coach/attendance"
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-amber-50 border border-amber-200 active:translate-y-[2px] transition-transform"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-amber-800 text-sm">{t("coach.attendanceTodo", "Prezență de completat azi")}</p>
                  <p className="text-xs text-amber-600">
                    {todayNeedsAttendance} {todayNeedsAttendance === 1 ? t("coach.sessionOne", "sesiune") : t("coach.sessionMany", "sesiuni")}
                  </p>
                </div>
                <svg className="w-5 h-5 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            )}
            {metricsNeeded > 0 && (
              <Link
                to="/app/coach/metrics"
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-violet-50 border border-violet-200 active:translate-y-[2px] transition-transform"
              >
                <div className="w-10 h-10 rounded-xl bg-violet-100 grid place-items-center shrink-0">
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-violet-800 text-sm">{t("coach.metricsTodo", "Metrici de actualizat")}</p>
                  <p className="text-xs text-violet-600">
                    {metricsNeeded} {metricsNeeded === 1 ? t("coach.athleteOne", "sportiv") : t("coach.athleteMany", "sportivi")}
                  </p>
                </div>
                <svg className="w-5 h-5 text-violet-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            )}
          </div>
        )}

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/app/coach/teams" className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{t("coach.teams", "Echipe")}</p>
                <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none mt-0.5">{teams.length}</p>
              </div>
            </div>
          </Link>

          <Link to="/app/coach/athletes" className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{t("coach.athletes", "Sportivi")}</p>
                <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none mt-0.5">{totalAthletes}</p>
              </div>
            </div>
          </Link>

          <Link to="/app/coach/sessions" className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{t("coach.upcoming", "Programate")}</p>
                <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none mt-0.5">{sessions.length}</p>
              </div>
            </div>
          </Link>

          <Link to="/app/coach/attendance" className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{t("coach.today", "Azi")}</p>
                <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none mt-0.5">{todaySessions.length}</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Today's Sessions */}
        {todaySessions.length > 0 && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
              <h2 className="font-bold text-amber-800">{t("coach.todaySessions", "Sesiunile de azi")}</h2>
            </div>
            <div className="p-4 space-y-3">
              {todaySessions.map(session => (
                <Link
                  key={session.id}
                  to={`/app/coach/attendance?session=${session.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex flex-col items-center justify-center text-white shadow-lg">
                    <span className="text-sm font-bold leading-none">
                      {new Date(session.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-800 truncate">{session.title}</h4>
                    <p className="text-xs text-slate-500">{session.team?.name}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"
                  }`}>
                    {session.type === "sport" ? "Sport" : "Educațional"}
                  </span>
                </Link>
              ))}
            </div>
          </GameCard>
        )}

        {/* Next Session */}
        {nextSession && !todaySessions.find(s => s.id === nextSession.id) && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <h2 className="font-bold text-blue-800">{t("coach.nextSession", "Următoarea sesiune")}</h2>
              <Link to="/app/coach/sessions" className="text-sm text-blue-600 font-medium">
                {t("common.viewAll", "Vezi toate")}
              </Link>
            </div>
            <div className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex flex-col items-center justify-center text-white shadow-lg">
                  <span className="text-lg font-bold leading-none">
                    {new Date(nextSession.start_at).getDate()}
                  </span>
                  <span className="text-xs uppercase">
                    {new Date(nextSession.start_at).toLocaleString("ro-RO", { month: "short" })}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{nextSession.title}</h3>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      {new Date(nextSession.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="truncate">{nextSession.team?.name}</span>
                  </div>
                  {nextSession.location_text && (
                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="truncate">{nextSession.location_text}</span>
                    </div>
                  )}
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  nextSession.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"
                }`}>
                  {nextSession.type === "sport" ? "Sport" : "Educațional"}
                </span>
              </div>
            </div>
          </GameCard>
        )}

        {/* Teams Overview */}
        {teams.length > 0 && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-bold text-slate-800">{t("coach.myTeams", "Echipele mele")}</h2>
              <Link to="/app/coach/teams" className="text-sm text-indigo-600 font-medium">
                {t("common.viewAll", "Vezi toate")}
              </Link>
            </div>
            <div className="p-4 space-y-3">
              {teams.slice(0, 4).map(team => (
                <Link
                  key={team.id}
                  to={`/app/coach/teams?team=${team.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-indigo-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-800 truncate">{team.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      {team.sport_type && (
                        <span className="text-xs text-slate-500">{sportName(team.sport_type)}</span>
                      )}
                      {team.age_group && (
                        <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{team.age_group}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-indigo-600">{team.athleteCount}</span>
                    <p className="text-xs text-slate-500">{t("coach.athletes", "sportivi")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </GameCard>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
